import { MDBTable, MDBTableBody, MDBTableHead } from "mdbreact";
import moment from "moment";
import { formatMoney } from "../../../helpers/formatter";
import { Button } from "antd";
import React, { Dispatch, SetStateAction, useState } from "react";
import searchIcon from "../../../assets/svg/icons/search-icon.svg";
import arrowDown from "../../../assets/svg/icons/arrow-down-black.svg";
import {
  IParams,
  useFetchNIPTransactions,
} from "../../../custom-hooks/useFetchNIPTransactions";
import styles from "../TransactionSetting.module.scss";
import Paginator from "../../Pagination/Pagination.component";
import { Loader } from "../../../UI/Loaders/Loaders";
import TransactionsFilter from "./modals/TransactionsFilter";

interface BankTransferTransaction {
  amount: number;
  beneficiaryAccountNumber: string;
  beneficiaryName: string;
  confirmationStatus: {
    name: string;
    id: number;
  };
  destinationBankCode: string;
  destinationBankName: string;
  feeAmount: number;
  originatorAccountNumber: string;
  originatorName: string;
  partnerCode: string;
  paymentReference: string;
  provider: {
    name: string;
    id: number;
  };
  sessionId: string;
  transactionDate: string; // ISO date string
  transactionStatus: {
    name: string;
    id: number;
  };
  vatAmount: number;
}

interface ITransactions {
  setShowTransactions: Dispatch<SetStateAction<boolean>>;
}

const TransactionsTable = () => {
  const [pageNumber, setPageNumber] = useState(1);

  const [query, setQuery] = useState<IParams>({
    page: 1,
    pageSize: 20,
    transactionStatus: "",
    startDate: "",
    endDate: "",
    sessionId: "",
    transferProvider: "",
  });
  const [showFilter, setShowFilter] = useState(false);

  const { data: transactionData, isLoading: isLoadingTransactionData } =
    useFetchNIPTransactions({
      page: pageNumber,
      pageSize: query.pageSize,
      transactionStatus: query.transactionStatus,
      startDate: query.startDate,
      endDate: query.endDate,
      sessionId: query.sessionId,
      transferProvider: query.transferProvider,
    });

  let RenderedComponent = null;
  if (isLoadingTransactionData) {
    RenderedComponent = (
      <div>
        <Loader centered={true} text="Loading transactions..." />
      </div>
    );
  } else if (transactionData?.data?.length === 0) {
    RenderedComponent = (
      <div className="centerLoader">
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt p-y-5">
            <i className="fas fa-bell" /> There are currently no transaction
            records.
          </h4>
        </div>
      </div>
    );
  } else {
    RenderedComponent = (
      <>
        <MDBTable
          responsive
          minimal="true"
          striped
          bordered
          hover
          id="table-to-xls"
        >
          <MDBTableHead>
            <tr>
              <th>Session ID</th>
              <th>Recipient</th>
              <th>Provider</th>
              <th>Amount</th>
              <th>Date Created</th>
              <th>Status</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            {transactionData?.data?.map(
              (transaction: BankTransferTransaction) => (
                <tr key={transaction.sessionId}>
                  <td>{transaction?.sessionId}</td>
                  <td>{transaction?.beneficiaryName}</td>
                  <td>{transaction?.provider?.name}</td>
                  <td>{formatMoney(transaction?.amount)}</td>
                  <td>
                    {transaction?.transactionDate
                      ? moment(transaction?.transactionDate).format(
                          "DD-MMM-YYYY, h:mm a",
                        )
                      : "Nil"}
                  </td>
                  <td>
                    <span
                      className={
                        transaction?.transactionStatus?.name?.toLowerCase() ===
                        "processing"
                          ? styles.processing
                          : transaction?.transactionStatus?.name?.toLowerCase() ===
                              "success"
                            ? styles.success
                            : styles.failed
                      }
                    >
                      {transaction?.transactionStatus?.name}
                    </span>
                  </td>
                </tr>
              ),
            )}
          </MDBTableBody>
        </MDBTable>
      </>
    );
  }

  return (
    <div className={styles.transactions_container}>
      <header className={styles.transactions_header_container}>
        <div className={styles.filter_container}>
          <div className={styles.search_input_container}>
            <img src={searchIcon} alt="" />
            <input
              placeholder="Search by session ID"
              className={styles.input_field}
              onChange={(e) =>
                setQuery({ ...query, sessionId: e.target.value })
              }
            />
          </div>
          <Button
            className={styles.filter_modal_trigger}
            onClick={() => setShowFilter(true)}
          >
            <span>Filter by</span>
            <img src={arrowDown} alt="" />
          </Button>
        </div>
      </header>

      <div className="">
        {RenderedComponent}
        {transactionData?.data?.length > 0 && (
          <Paginator
            size={query.pageSize}
            page={query.page}
            count={transactionData?.totalCount}
            changeCurrentPage={(pageNumber: number) =>
              setPageNumber(pageNumber)
            }
          />
        )}
      </div>

      <TransactionsFilter
        open={showFilter}
        title="Filter Transactions"
        handleCancel={() => setShowFilter(false)}
        confirmBtnText="Apply"
        cancelBtnTxt="Cancel"
        customActionBtnTxt="Reset"
        setQuery={setQuery as Dispatch<SetStateAction<IParams>>}
        query={query}
        setPageNumber={setPageNumber}
      />
    </div>
  );
};

export default TransactionsTable;
